/** Material UI Packages. */
import { GridActionsCellItem } from '@mui/x-data-grid';

/** Material UI Packages. */
import { Typography, Link, Tooltip } from '@mui/material';

/** Material UI Icons. */
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

/** Internal Components. */
import { prealert_status, stage_status, delivery_order_status, invoice_crdr_status } from 'shared/config/constantsData';
import {
    CustomSelectCell_string,
    CustomAutocompleteCell_string,
    CustomAutocompleteCell_TradePartner,
    FillDownHeader,
    MultiLinkCell,
    MultiLinkEditCell,
    DateTypingEditCell,
} from 'shared/ui/RenderCell';
import { customized_style } from 'shared/config/Styles';
import { createHeaderGroup, createMultiHeaderGroup } from 'shared/ui/Tooltip';
import { getClassName, getHeaderClass } from 'shared/lib/todoColorHandler';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import {
    createOptionPasteParser,
    createTradePartnerValueParser,
    tradePartnerValueFormatter,
    parsePastedDate,
    objectValueFormatter,
    createObjectValueParser,
} from 'shared/lib/datagridHelpers';
import { isAssistantManagerOrAbove } from 'shared/lib/roles';
import GoogleRenderCell from 'shared/ui/GoogleRenderCell';

export const hblCol = (
    userRole,
    presetData,
    tab,
    todo,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
    handleFormatMismatch,
) => {
    const preAlert = presetData?.pre_alert;
    const invoice = presetData?.invoice;
    const crdr = presetData?.crdr;

    const filtered_todo = todo?.filter((item) => item.filter_id === tab.id);
    let isHighlighted_prealert = filtered_todo?.length ? getClassName(filtered_todo, ['pre_alert'], 'header') : '';
    let isHighlighted_invoice = filtered_todo?.length ? getClassName(filtered_todo, ['invoice'], 'header') : '';
    let isHighlighted_crdr = filtered_todo?.length ? getClassName(filtered_todo, ['crdr'], 'header') : '';

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },
        {
            field: 'il_trade_partner_id_shipper',
            headerName: 'Shipper',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            // Copy/paste carries the whole partner object as JSON (same pattern as the bulk dialog
            // and location cells); a plain name or a location object is rejected before it can be saved.
            valueFormatter: objectValueFormatter,
            valueParser: createObjectValueParser({ key: 'il_trade_partner_id_shipper', label: 'Shipper' }, handleFormatMismatch),
            renderCell: (params) => {
                return (
                    <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id_shipper.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'il_trade_partner_id_consignee',
            headerName: 'Consignee',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            // Copy/paste carries the whole partner object as JSON (same pattern as the bulk dialog
            // and location cells); a plain name or a location object is rejected before it can be saved.
            valueFormatter: objectValueFormatter,
            valueParser: createObjectValueParser({ key: 'il_trade_partner_id_consignee', label: 'Consignee' }, handleFormatMismatch),
            renderCell: (params) => {
                return (
                    <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id_consignee.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'hawb_link',
            headerName: 'Link',
            flex: 0.7,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Link href={params.value ? `${params.value}` : '#'} target="_blank" rel="noopener">
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'reference',
            headerName: 'Reference',
            flex: 0.7,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>
            ),
        },
        {
            field: 'pre_alert',
            headerName: 'Status',
            flex: 1,
            editable: preAlert,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: preAlert ? '' : 'disabled-header',
            headerClassName: getHeaderClass(preAlert, isHighlighted_prealert),
            renderCell: (params) => {
                return <span style={{ color: preAlert ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={prealert_status} />,
        },
        {
            field: 'invoice',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            valueOptions: invoice_crdr_status,
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            renderCell: (params) => {
                return <span style={{ color: invoice ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => {
                const allowedStatuses = ['Pending', 'Ready', 'Sent'];
                const hasOperationManagerRole =
                    isAssistantManagerOrAbove(userRole);
                return (
                    <CustomSelectCell_string
                        params={params}
                        options={invoice_crdr_status}
                        hasOperationManagerRole={hasOperationManagerRole}
                        allowedStatuses={allowedStatuses}
                    />
                );
            },
        },
        {
            field: 'invoice_link',
            headerName: 'Link',
            flex: 0.7,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            renderCell: (params) => <MultiLinkCell params={params} enabled={invoice} />,
            renderEditCell: (params) => <MultiLinkEditCell params={params} />,
        },
        {
            field: 'invoice_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.7,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: invoice ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'crdr',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            valueOptions: invoice_crdr_status,
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            renderCell: (params) => {
                return <span style={{ color: crdr ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => {
                const allowedStatuses = ['Pending', 'Ready', 'Sent'];
                const hasOperationManagerRole =
                    isAssistantManagerOrAbove(userRole);
                return (
                    <CustomSelectCell_string
                        params={params}
                        options={invoice_crdr_status}
                        hasOperationManagerRole={hasOperationManagerRole}
                        allowedStatuses={allowedStatuses}
                    />
                );
            },
        },
        {
            field: 'crdr_link',
            headerName: 'Link',
            flex: 0.7,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            renderCell: (params) => <MultiLinkCell params={params} enabled={crdr} />,
            renderEditCell: (params) => <MultiLinkEditCell params={params} />,
        },
        {
            field: 'crdr_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.7,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: crdr ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
    ];
};

export const hblAliasCol = (
    handleDeleteClick,
    presetData,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
    matchedTodoContainer,
    handleLinkClick,
    mainRow,
    handleFillDown,
    onInvalidOption,
) => {
    const deliveryOrder = presetData?.delivery_order;
    const stages = presetData?.stages;

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },

        {
            field: 'alias',
            headerName: 'Alias',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => <FillDownHeader label="Alias" field="alias" onFillDown={handleFillDown} />,
        },
        {
            field: 'il_opus_vessel_id',
            headerName: 'Flight#',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => <FillDownHeader label="Flight#" field="il_opus_vessel_id" onFillDown={handleFillDown} />,
        },
        {
            field: 'quantity',
            headerName: 'Vol',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            renderHeader: () => <FillDownHeader label="Vol" field="quantity" onFillDown={handleFillDown} />,
        },
        {
            field: 'il_opus_volume_unit_id',
            headerName: 'Unit',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <FillDownHeader label="Unit" field="il_opus_volume_unit_id" onFillDown={handleFillDown} />
            ),
            renderEditCell: (params) => (
                <CustomAutocompleteCell_string
                    params={params}
                    options={unitOptions}
                    handleInputChange={(event, value) => handleUnitChange(event, value)}
                    shouldFocus={true}
                />
            ),
        },
        {
            field: 'hazmat',
            headerName: 'Hazmat',
            flex: 0.7,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'boolean',
            renderHeader: () => <FillDownHeader label="Hazmat" field="hazmat" onFillDown={handleFillDown} />,
            valueGetter: (value) => value === true,
        },
        {
            field: 'weight',
            headerName: 'Weight',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            renderHeader: () => <FillDownHeader label="Weight" field="weight" onFillDown={handleFillDown} />,
            renderCell: (params) => `${params.value || 0} kg`,
        },
        {
            field: 'il_location_id_pick_up',
            headerName: 'Pick Up',
            flex: 1.2,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Pick Up" field="il_location_id_pick_up" onFillDown={handleFillDown} />
            ),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => (
                <Typography
                    sx={{ ...customized_style.itemDatagrid_Typography, color: deliveryOrder ? 'inherit' : '#C7C7C7' }}
                >
                    {params.value}
                </Typography>
            ),
            renderEditCell: (params) => (
                <GoogleRenderCell
                    params={params}
                    shouldFocus={true}
                    reducer={'setAexContainerFormData'}
                    tableName={'il_air_export_hbl_alias'}
                    hasRowUpdate={false}
                    tableId={[mainRow.id, params.row.il_air_export_hbl_id]}
                />
            ),
        },
        {
            field: 'il_trade_partner_id',
            headerName: 'Trucker Name',
            flex: 1,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Trucker Name" field="il_trade_partner_id" onFillDown={handleFillDown} />
            ),
            valueParser: createTradePartnerValueParser(
                'il_trade_partner_id',
                tradePartnerOptions?.il_trade_partner_id?.options,
            ),
            valueFormatter: tradePartnerValueFormatter,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: deliveryOrder ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {params?.value?.name || ''}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'delivery_order',
            headerName: 'Status',
            flex: 1,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="delivery_order" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(delivery_order_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: deliveryOrder ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={delivery_order_status} />,
        },
        {
            field: 'delivery_order_link',
            headerName: 'Link',
            flex: 0.6,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Link" field="delivery_order_link" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !deliveryOrder ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!deliveryOrder) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'stages',
            headerName: 'Status',
            flex: 1,
            editable: stages,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: stages ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="stages" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(stage_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'stages', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: stages ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={stage_status} />,
        },

        {
            field: 'stages_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.6,
            editable: stages,
            align: 'center',
            headerAlign: 'center',
            headerClassName: stages ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Date" field="stages_date" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'stages', 'header-cell');
            },
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: stages ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.3,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'actions',
            getActions: ({ id }) => {
                return [
                    <Tooltip title="Delete">
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={() => handleDeleteClick(id)}
                            color="inherit"
                        />
                    </Tooltip>,
                ];
            },
        },
    ];
};

export const hblGroupingModel = (presetData, tabValue, historyData, tab) => {
    const preAlert = presetData?.pre_alert;
    const invoice = presetData?.invoice;
    const crdr = presetData?.crdr;

    return [
        {
            groupId: 'HAWB',
            headerAlign: 'center',
            children: [
                { field: 'il_trade_partner_id_shipper' },
                { field: 'il_trade_partner_id_consignee' },
                { field: 'hawb_link' },
                { field: 'reference' },
            ],
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, [
                    'il_trade_partner_id_shipper',
                    'il_trade_partner_id_consignee',
                    'hawb_link',
                    'reference',
                ]),
        },
        {
            groupId: 'Pre Alert',
            headerAlign: 'center',
            children: [{ field: 'pre_alert' }],
            headerClassName: preAlert ? '' : 'disabled-header',
            renderHeaderGroup: (params) => createHeaderGroup(params, historyData, tabValue, tab, ['pre_alert']),
        },
        {
            groupId: 'Invoice',
            headerAlign: 'center',
            children: [{ field: 'invoice' }, { field: 'invoice_link' }, { field: 'invoice_date' }],
            headerClassName: invoice ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['invoice', 'invoice_link', 'invoice_date']),
        },
        {
            groupId: 'Credit/Debit Note',
            headerAlign: 'center',
            children: [{ field: 'crdr' }, { field: 'crdr_link' }, { field: 'crdr_date' }],
            headerClassName: crdr ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['crdr', 'crdr_link', 'crdr_date']),
        },
    ];
};

export const hblAliasGroupingModel = (presetData, tabValue, historyData, tab) => {
    const deliveryOrder = presetData?.delivery_order;
    const stages = presetData?.stages;

    return [
        {
            groupId: 'Air Cargo',
            headerAlign: 'center',
            children: [
                { field: 'alias' },
                { field: 'il_opus_vessel_id' },
                { field: 'quantity' },
                { field: 'il_opus_volume_unit_id' },
                { field: 'hazmat' },
                { field: 'weight' },
            ],
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'alias',
                    'il_opus_vessel_id',
                    'quantity',
                    'il_opus_volume_unit_id',
                    'hazmat',
                    'weight',
                ]),
        },
        {
            groupId: 'Delivery Order',
            headerAlign: 'center',
            children: [
                { field: 'il_location_id_pick_up' },
                { field: 'il_trade_partner_id' },
                { field: 'delivery_order' },
                { field: 'delivery_order_link' },
            ],
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'il_location_id_pick_up',
                    'delivery_order',
                    'delivery_order_link',
                    'il_trade_partner_id',
                ]),
        },
        {
            groupId: 'Stages',
            headerAlign: 'center',
            children: [{ field: 'stages' }, { field: 'stages_date' }],
            headerClassName: stages ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, ['stages', 'stages_date']),
        },
    ];
};
